import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated, getMustChangePassword, getCurrentUserProfile } from '../services/api';

/**
 * EmployeeProtectedRoute component that ensures:
 * 1. User is authenticated
 * 2. User has changed their password (if required)
 * 3. User is an EMPLOYE (not ADMIN or MANAGER)
 */
const EmployeeProtectedRoute = ({ children }) => {
  const location = useLocation();
  const [isAuthorized, setIsAuthorized] = useState(null);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const checkAuthorization = async () => {
      // Check authentication
      if (!isAuthenticated()) {
        setIsAuthorized(false);
        return;
      }

      // Check if user must change password
      if (getMustChangePassword()) {
        setIsAuthorized(false);
        return;
      }

      // Check user role
      try {
        const userData = await getCurrentUserProfile();
        const role = userData?.role;
        setUserRole(role);
        
        // Only EMPLOYE can access employee routes
        if (role === 'EMPLOYE') {
          // Check if employee has a centre assigned
          if (!userData.centre_id && !userData.centre) {
            setIsAuthorized(false);
          } else {
            setIsAuthorized(true);
          }
        } else {
          // Admin or Manager trying to access employee route - not authorized
          setIsAuthorized(false);
        }
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
        setIsAuthorized(false);
      }
    };

    checkAuthorization();
  }, []);

  // Check authentication
  if (!isAuthenticated()) {
    // Redirect to login, preserving the attempted location
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user must change password
  if (getMustChangePassword()) {
    // Redirect to reset-password, preserving the attempted location
    return <Navigate to="/reset-password" state={{ from: location }} replace />;
  }

  // Check authorization - if not authorized, redirect based on role
  if (isAuthorized === false) {
    if (userRole === 'ADMIN') {
      return <Navigate to="/admin-dashboard" replace />;
    } else if (userRole === 'MANAGER') {
      return <Navigate to="/manager-dashboard" replace />;
    } else if (userRole === 'EMPLOYE') {
      // Employee without centre - redirect to profile page
      return <Navigate to="/profile" replace />;
    } else {
      // Fallback to login if role is unknown
      return <Navigate to="/login" replace />;
    }
  }

  // User is authenticated, has changed password, and is authorized (EMPLOYE) - allow access
  // Render children immediately while role check happens in background
  return children;
};

export default EmployeeProtectedRoute;

